/**
 * Download Progress Component
 *
 * Displays real-time model download progress for containers.
 * Features:
 * - Progress bars with percentage and speed
 * - ETA display
 * - Status indicators (pending, downloading, completed, failed)
 * - Collapsible per-node grouping
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getApiClient, isSuccess } from '../../api/client';
import { useWebSocket } from '../../hooks/useWebSocket';

// =============================================================================
// Types
// =============================================================================

export interface ContainerProgress {
  id: string;
  nodeId: string;
  containerId: string;
  type: 'model_download' | 'model_load' | 'container_startup';
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  model?: string;
  percentage: number;
  totalBytes: number;
  downloadedBytes: number;
  speedBps: number;
  etaSeconds: number;
  message?: string;
  error?: string;
  startedAt?: string;
  completedAt?: string;
  updatedAt: string;
}

export interface ProgressEvent {
  type: 'container:progress';
  nodeId: string;
  containerId: string;
  data: ContainerProgress;
}

interface ProgressResponse {
  progress: ContainerProgress[];
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format bytes to human-readable string
 */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

/**
 * Format speed in bytes per second to human-readable string
 */
function formatSpeed(bps: number): string {
  if (bps === 0) return '0 B/s';
  const k = 1024;
  const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
  const i = Math.floor(Math.log(bps) / Math.log(k));
  return `${(bps / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

/**
 * Format ETA in seconds to human-readable string
 */
function formatEta(seconds: number): string {
  if (seconds <= 0 || !isFinite(seconds)) return '--';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
}

/**
 * Get status color classes
 */
function getStatusColors(status: ContainerProgress['status']): {
  bg: string;
  text: string;
  progress: string;
} {
  switch (status) {
    case 'pending':
      return { bg: 'bg-gray-900/20', text: 'text-gray-400', progress: 'bg-gray-500' };
    case 'in_progress':
      return { bg: 'bg-blue-900/20', text: 'text-blue-400', progress: 'bg-blue-500' };
    case 'completed':
      return { bg: 'bg-green-900/20', text: 'text-green-400', progress: 'bg-green-500' };
    case 'failed':
      return { bg: 'bg-red-900/20', text: 'text-red-400', progress: 'bg-red-500' };
    case 'cancelled':
      return { bg: 'bg-yellow-900/20', text: 'text-yellow-400', progress: 'bg-yellow-500' };
    default:
      return { bg: 'bg-gray-900/20', text: 'text-gray-400', progress: 'bg-gray-500' };
  }
}

/**
 * Get progress type label
 */
function getTypeLabel(type: ContainerProgress['type']): string {
  switch (type) {
    case 'model_download':
      return 'Downloading';
    case 'model_load':
      return 'Loading';
    case 'container_startup':
      return 'Starting';
    default:
      return type;
  }
}

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Status badge component
 */
function StatusBadge({ status }: { status: ContainerProgress['status'] }) {
  const colors = getStatusColors(status);
  const labels: Record<ContainerProgress['status'], string> = {
    pending: 'Pending',
    in_progress: 'In Progress',
    completed: 'Completed',
    failed: 'Failed',
    cancelled: 'Cancelled',
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors.bg} ${colors.text}`}>
      {labels[status]}
    </span>
  );
}

/**
 * Individual progress item component
 */
interface ProgressItemProps {
  progress: ContainerProgress;
  showNode?: boolean;
  nodeName?: string;
}

function ProgressItem({ progress, showNode, nodeName }: ProgressItemProps) {
  const colors = getStatusColors(progress.status);
  const isActive = progress.status === 'in_progress' || progress.status === 'pending';

  return (
    <div className={`rounded-lg border border-discord-light p-3 ${colors.bg}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white truncate">
              {progress.model || progress.containerId}
            </span>
            <StatusBadge status={progress.status} />
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
            <span>{getTypeLabel(progress.type)}</span>
            {showNode && nodeName && (
              <>
                <span>-</span>
                <span>{nodeName}</span>
              </>
            )}
          </div>
        </div>
        {isActive && (
          <div className="text-right text-xs">
            <div className="text-white font-medium">{progress.percentage.toFixed(1)}%</div>
            <div className="text-gray-500">{formatEta(progress.etaSeconds)}</div>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="h-2 bg-discord-light rounded-full overflow-hidden">
        <div
          className={`h-full ${colors.progress} transition-all duration-300 ${
            isActive ? 'animate-pulse' : ''
          }`}
          style={{ width: `${Math.min(progress.percentage, 100)}%` }}
        />
      </div>

      {/* Details */}
      <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
        <span>
          {formatBytes(progress.downloadedBytes)} / {formatBytes(progress.totalBytes)}
        </span>
        {isActive && progress.speedBps > 0 && (
          <span className="flex items-center gap-1">
            <DownloadIcon />
            {formatSpeed(progress.speedBps)}
          </span>
        )}
      </div>

      {/* Error Message */}
      {progress.error && (
        <div className="mt-2 p-2 rounded bg-red-900/30 border border-red-500/50">
          <p className="text-xs text-red-300">{progress.error}</p>
        </div>
      )}

      {/* Status Message */}
      {progress.message && !progress.error && (
        <p className="mt-1 text-xs text-gray-500">{progress.message}</p>
      )}
    </div>
  );
}

/**
 * Download icon component
 */
function DownloadIcon() {
  return (
    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
      />
    </svg>
  );
}

/**
 * Empty state component
 */
function EmptyState() {
  return (
    <div className="text-center py-8">
      <svg
        className="mx-auto h-10 w-10 text-gray-500 mb-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
        />
      </svg>
      <p className="text-sm text-gray-400">No active downloads</p>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export interface DownloadProgressProps {
  /** Filter by specific node ID */
  nodeId?: string;
  /** Show completed downloads */
  showCompleted?: boolean;
  /** Maximum items to display */
  maxItems?: number;
  /** Compact display mode */
  compact?: boolean;
  /** Node names map for display */
  nodeNames?: Map<string, string>;
}

/**
 * Download Progress component
 *
 * Displays real-time progress for model downloads and container operations.
 * Subscribes to WebSocket events for live updates.
 */
export function DownloadProgress({
  nodeId,
  showCompleted = false,
  maxItems = 10,
  compact = false,
  nodeNames = new Map(),
}: DownloadProgressProps) {
  const { accessToken } = useAuth();
  const [progress, setProgress] = useState<ContainerProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // WebSocket subscription
  const { subscribe, isConnected } = useWebSocket({
    accessToken,
    autoConnect: true,
  });

  /**
   * Fetch initial progress data
   */
  const fetchProgress = useCallback(async () => {
    const apiClient = getApiClient();
    setIsLoading(true);
    setError(null);

    try {
      const endpoint = nodeId
        ? `/nodes/${nodeId}/progress`
        : '/nodes/dashboard/progress';

      const result = await apiClient.get<ProgressResponse>(endpoint);

      if (isSuccess(result)) {
        setProgress(result.data.progress || []);
      } else {
        setError(result.error.message || 'Failed to fetch progress');
      }
    } catch {
      setError('Failed to load progress data');
    } finally {
      setIsLoading(false);
    }
  }, [nodeId]);

  /**
   * Handle WebSocket progress update
   */
  const handleProgressUpdate = useCallback(
    (event: ProgressEvent['data']) => {
      // Filter by nodeId if specified
      if (nodeId && event.nodeId !== nodeId) {
        return;
      }

      setProgress((prev) => {
        const existingIndex = prev.findIndex(
          (p) =>
            p.nodeId === event.nodeId &&
            p.containerId === event.containerId &&
            p.type === event.type
        );

        if (existingIndex >= 0) {
          // Update existing progress
          const updated = [...prev];
          updated[existingIndex] = event;
          return updated;
        } else {
          // Add new progress
          return [event, ...prev];
        }
      });
    },
    [nodeId]
  );

  /**
   * Initial data fetch
   */
  useEffect(() => {
    void fetchProgress();
  }, [fetchProgress]);

  /**
   * Subscribe to progress events
   */
  useEffect(() => {
    const unsubscribe = subscribe<ProgressEvent['data']>(
      'container:progress',
      handleProgressUpdate
    );
    return unsubscribe;
  }, [subscribe, handleProgressUpdate]);

  /**
   * Filter and sort progress items
   */
  const filteredProgress = useMemo(() => {
    let items = progress;

    // Filter out completed if not showing them
    if (!showCompleted) {
      items = items.filter(
        (p) => p.status === 'pending' || p.status === 'in_progress'
      );
    }

    // Sort: in_progress first, then pending, then by updatedAt
    items.sort((a, b) => {
      const statusOrder = { in_progress: 0, pending: 1, failed: 2, completed: 3, cancelled: 4 };
      const aOrder = statusOrder[a.status] ?? 5;
      const bOrder = statusOrder[b.status] ?? 5;

      if (aOrder !== bOrder) return aOrder - bOrder;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

    // Limit items
    return items.slice(0, maxItems);
  }, [progress, showCompleted, maxItems]);

  /**
   * Group progress by node
   */
  const groupedProgress = useMemo(() => {
    const groups = new Map<string, ContainerProgress[]>();

    for (const item of filteredProgress) {
      const existing = groups.get(item.nodeId) || [];
      existing.push(item);
      groups.set(item.nodeId, existing);
    }

    return groups;
  }, [filteredProgress]);

  // Loading state
  if (isLoading && progress.length === 0) {
    return (
      <div className="animate-pulse space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-lg bg-discord-darker border border-discord-light p-3">
            <div className="h-4 w-3/4 bg-discord-light rounded mb-2" />
            <div className="h-2 w-full bg-discord-light rounded" />
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error && progress.length === 0) {
    return (
      <div className="rounded-lg bg-red-900/20 border border-red-500/50 p-4">
        <p className="text-sm text-red-300">{error}</p>
        <button
          onClick={() => void fetchProgress()}
          className="mt-2 text-xs text-red-400 hover:text-red-300"
        >
          Retry
        </button>
      </div>
    );
  }

  // Empty state
  if (filteredProgress.length === 0) {
    return <EmptyState />;
  }

  // Compact display (single list)
  if (compact || nodeId) {
    return (
      <div className="space-y-2">
        {/* Connection indicator */}
        {isConnected && (
          <div className="flex items-center gap-1 text-xs text-green-400 mb-2">
            <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" />
            Live updates
          </div>
        )}

        {filteredProgress.map((item) => (
          <ProgressItem
            key={`${item.nodeId}-${item.containerId}-${item.type}`}
            progress={item}
            showNode={!nodeId}
            nodeName={nodeNames.get(item.nodeId)}
          />
        ))}
      </div>
    );
  }

  // Grouped display
  return (
    <div className="space-y-4">
      {/* Connection indicator */}
      {isConnected && (
        <div className="flex items-center gap-1 text-xs text-green-400">
          <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" />
          Live updates
        </div>
      )}

      {Array.from(groupedProgress.entries()).map(([groupNodeId, items]) => (
        <div key={groupNodeId} className="space-y-2">
          <h4 className="text-sm font-medium text-gray-400">
            {nodeNames.get(groupNodeId) || groupNodeId}
          </h4>
          {items.map((item) => (
            <ProgressItem
              key={`${item.nodeId}-${item.containerId}-${item.type}`}
              progress={item}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export default DownloadProgress;
